<script lang="ts" setup>
import { computed, ref, shallowRef, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/vue';
import { IconPlus, IconMinus, IconClose } from '@/icons';
import SkeletonRectangle from '@/app/components/skeleton-rectangle/skeleton-rectangle.vue';
import { UiButton } from '../ui-button';
import { UiButtonIcon } from '../ui-button-icon';
import type { ProductCardBarItem } from './interfaces';

interface Props {
  items: ProductCardBarItem[];
  editable?: boolean;
  applyLabel?: string;
  valueDisplay?: 'diff' | 'fraction';
  orientation?: 'horizontal' | 'vertical';
  verticalMetric?: 'value' | 'allocated';
  showMax?: boolean;
  fillVariant?: 'neutral' | 'accent';
  loading?: boolean;
  mode?: 'default' | 'stacked';
}

const props = withDefaults(defineProps<Props>(), {
  editable: false,
  applyLabel: 'Apply',
  valueDisplay: 'diff',
  orientation: 'horizontal',
  verticalMetric: 'allocated',
  showMax: true,
  fillVariant: 'neutral',
  loading: false,
  mode: 'default',
});

const emit = defineEmits<{
  apply: [index: number, value: number];
}>();

const hasDiffMode = computed(() => props.items.some((item) => item.allocated != null));
const isStacked = computed(() => props.mode === 'stacked');
const stackedHover = ref<number | null>(null);
const stackedMax = computed(() => {
  if (!isStacked.value) return 1;
  return Math.max(
    ...props.items.map((item) => {
      const total = (item.inventory ?? 0) + (item.replenishment ?? 0);
      return Math.max(total, item.target ?? 0);
    }),
    1,
  );
});
const openIndex = ref<number | null>(null);
const editValue = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);
const rowRefs = ref<(HTMLElement | null)[]>([]);
const popoverRef = shallowRef<HTMLElement | null>(null);
const valueEditorInstanceId = `ui-product-card-bar-editor-${Math.random().toString(36).slice(2)}`;

const activeRowRef = computed(() => {
  if (openIndex.value == null) return null;
  return rowRefs.value[openIndex.value] ?? null;
});

const { floatingStyles } = useFloating(activeRowRef, popoverRef, {
  placement: computed(() => (props.orientation === 'vertical' ? 'top' : 'right-start')),
  middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
  open: computed(() => openIndex.value != null),
  transform: false,
});

function onDocumentPointerDown(event: PointerEvent) {
  if (openIndex.value == null) {
    return;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return;
  }
  if (popoverRef.value?.contains(target)) {
    return;
  }
  const clickedRow = target.closest('[data-bar-row]') as HTMLElement | null;
  const isOwnRow = clickedRow != null && rowRefs.value.some((refEl) => refEl === clickedRow);
  if (isOwnRow) {
    return;
  }
  closeEdit();
}

function onGlobalValueEditorOpen(event: Event) {
  const detail = (
    event as CustomEvent<{
      sourceId?: string;
    }>
  ).detail;
  if (detail?.sourceId === valueEditorInstanceId) {
    return;
  }
  closeEdit();
}

onMounted(() => {
  window.addEventListener(
    'ui-product-card-value-editor-open',
    onGlobalValueEditorOpen as EventListener,
  );
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
});

onBeforeUnmount(() => {
  window.removeEventListener(
    'ui-product-card-value-editor-open',
    onGlobalValueEditorOpen as EventListener,
  );
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
});

function barPercent(value: number, max: number): string {
  if (!max) return '0%';
  return `${Math.min((value / max) * 100, 100)}%`;
}

function resolveVerticalValue(item: ProductCardBarItem) {
  if (props.verticalMetric === 'allocated' && typeof item.allocated === 'number') {
    return item.allocated;
  }
  return item.value;
}

function resolveVerticalMax(item: ProductCardBarItem) {
  if (Number.isFinite(item.max) && item.max > 0) {
    return item.max;
  }
  const value = resolveVerticalValue(item);
  return value > 0 ? value : 1;
}

function isVerticalAccent(item: ProductCardBarItem) {
  return props.verticalMetric === 'allocated' && typeof item.allocated === 'number';
}

function openEdit(index: number) {
  window.dispatchEvent(
    new CustomEvent('ui-product-card-value-editor-open', {
      detail: { sourceId: valueEditorInstanceId },
    }),
  );
  editValue.value = props.items[index].allocated ?? props.items[index].value;
  openIndex.value = index;
  nextTick(() => inputRef.value?.select());
}

function closeEdit() {
  openIndex.value = null;
}

function increment() {
  editValue.value++;
}

function decrement() {
  if (editValue.value > 0) editValue.value--;
}

function applyEdit() {
  if (openIndex.value == null) return;
  emit('apply', openIndex.value, editValue.value);
  closeEdit();
}

function onInputKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    applyEdit();
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    closeEdit();
  }
}

function setRowRef(index: number) {
  return (el: any) => {
    rowRefs.value[index] = el as HTMLElement;
  };
}
</script>

<template>
  <div
    :class="[$style.root, { [$style.rootVertical]: props.orientation === 'vertical' }]"
    data-ui-component="UiProductCardBarChart"
  >
    <template v-if="props.loading && props.orientation === 'horizontal'">
      <div v-for="(item, index) in props.items" :key="index" :class="$style.rowStatic">
        <span :class="$style.label">{{ item.label }}</span>
        <div :class="$style.barArea">
          <SkeletonRectangle :class="$style.loadingBar" />
        </div>
        <SkeletonRectangle :class="$style.loadingValue" />
      </div>
    </template>

    <div v-else-if="props.loading" :class="$style.columns">
      <div v-for="(item, index) in props.items" :key="index" :class="$style.columnStatic">
        <SkeletonRectangle :class="$style.loadingColumnValue" />
        <div :class="$style.columnTrackWrap">
          <SkeletonRectangle :class="$style.loadingColumnBar" />
        </div>
        <span :class="$style.columnLabel">{{ item.label }}</span>
      </div>
    </div>

    <template v-else-if="isStacked && props.orientation === 'horizontal'">
      <div
        v-for="(item, index) in props.items"
        :key="index"
        :class="$style.stackedRow"
        @mouseenter="stackedHover = index"
        @mouseleave="stackedHover = null"
      >
        <span :class="$style.label">{{ item.label }}</span>
        <div :class="$style.stackedBarArea">
          <div :class="$style.stackedTrack">
            <div
              :class="$style.fillStacked2"
              :style="{ width: barPercent((item.inventory ?? 0) + (item.replenishment ?? 0), stackedMax) }"
            />
            <div
              :class="$style.fillStacked1"
              :style="{ width: barPercent(item.inventory ?? 0, stackedMax) }"
            />
          </div>
          <div
            v-if="item.target"
            :class="$style.targetMark"
            :style="{ left: barPercent(item.target, stackedMax) }"
          />
        </div>
        <div v-if="stackedHover === index" :class="$style.stackedTooltip">
          <div :class="$style.stackedTooltipRow">
            <span :class="$style.stackedLegendSwatch1" />
            <span>Inventory</span>
            <span :class="$style.stackedTooltipVal">{{ item.inventory ?? 0 }}</span>
          </div>
          <div :class="$style.stackedTooltipRow">
            <span :class="$style.stackedLegendSwatch2" />
            <span>Replenishment</span>
            <span :class="$style.stackedTooltipVal">+{{ item.replenishment ?? 0 }}</span>
          </div>
          <div v-if="item.target" :class="$style.stackedTooltipRow">
            <span :class="$style.stackedLegendTarget" />
            <span>Target</span>
            <span :class="$style.stackedTooltipVal">{{ item.target }}</span>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="props.orientation === 'horizontal'">
      <template v-for="(item, index) in props.items" :key="index">
        <button
          v-if="props.editable"
          :ref="setRowRef(index)"
          type="button"
          data-bar-row
          :class="[$style.row, { [$style.rowActive]: openIndex === index }]"
          @click="openEdit(index)"
        >
          <span :class="$style.label">{{ item.label }}</span>
          <div :class="$style.barArea">
            <template v-if="!hasDiffMode">
              <div :class="$style.track">
                <div
                  :class="props.fillVariant === 'accent' ? $style.fillAccent : $style.fillDark"
                  :style="{ width: barPercent(item.value, item.max) }"
                />
              </div>
            </template>
            <template v-else-if="props.valueDisplay === 'fraction'">
              <div :class="$style.track">
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillAccent"
                  :style="{ width: barPercent(item.allocated, item.value) }"
                />
              </div>
            </template>
            <template v-else>
              <div :class="$style.track">
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillShade"
                  :style="{ width: barPercent(item.value, item.max) }"
                />
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillAccent"
                  :style="{ width: barPercent(item.allocated, item.max) }"
                />
              </div>
            </template>
          </div>
          <template v-if="hasDiffMode && props.valueDisplay === 'fraction'">
            <span :class="$style.value">{{ item.allocated ?? 0 }}</span>
            <span v-if="props.showMax" :class="$style.max">/{{ item.value }}</span>
          </template>
          <template v-else-if="hasDiffMode">
            <span
              :class="[
                $style.diff,
                {
                  [$style.diffPositive]: typeof item.diff === 'string' && item.diff.startsWith('+'),
                  [$style.diffNeutral]: item.diff === '-' || item.diff == null,
                },
              ]"
            >
              {{ item.diff == null || item.diff === '-' ? '0' : item.diff }}
            </span>
          </template>
          <template v-else>
            <span :class="$style.value">{{ item.value }}</span>
            <span v-if="props.showMax" :class="$style.max">/{{ item.max }}</span>
          </template>
        </button>

        <div v-else :class="$style.rowStatic">
          <span :class="$style.label">{{ item.label }}</span>
          <div :class="$style.barArea">
            <template v-if="!hasDiffMode">
              <div :class="$style.track">
                <div
                  :class="props.fillVariant === 'accent' ? $style.fillAccent : $style.fillDark"
                  :style="{ width: barPercent(item.value, item.max) }"
                />
              </div>
            </template>
            <template v-else-if="props.valueDisplay === 'fraction'">
              <div :class="$style.track">
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillAccent"
                  :style="{ width: barPercent(item.allocated, item.value) }"
                />
              </div>
            </template>
            <template v-else>
              <div :class="$style.track">
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillShade"
                  :style="{ width: barPercent(item.value, item.max) }"
                />
                <div
                  v-if="item.allocated != null"
                  :class="$style.fillAccent"
                  :style="{ width: barPercent(item.allocated, item.max) }"
                />
              </div>
            </template>
          </div>
          <template v-if="hasDiffMode && props.valueDisplay === 'fraction'">
            <span :class="$style.value">{{ item.allocated ?? 0 }}</span>
            <span v-if="props.showMax" :class="$style.max">/{{ item.value }}</span>
          </template>
          <template v-else-if="hasDiffMode">
            <span
              :class="[
                $style.diff,
                {
                  [$style.diffPositive]: typeof item.diff === 'string' && item.diff.startsWith('+'),
                  [$style.diffNeutral]: item.diff === '-' || item.diff == null,
                },
              ]"
            >
              {{ item.diff == null || item.diff === '-' ? '0' : item.diff }}
            </span>
          </template>
          <template v-else>
            <span :class="$style.value">{{ item.value }}</span>
            <span v-if="props.showMax" :class="$style.max">/{{ item.max }}</span>
          </template>
        </div>
      </template>
    </template>

    <div v-else :class="$style.columns">
      <template v-for="(item, index) in props.items" :key="index">
        <button
          v-if="props.editable"
          :ref="setRowRef(index)"
          type="button"
          data-bar-row
          :class="[$style.column, { [$style.rowActive]: openIndex === index }]"
          @click="openEdit(index)"
        >
          <span :class="$style.columnValue">{{ resolveVerticalValue(item) }}</span>
          <div :class="$style.columnTrackWrap">
            <div :class="$style.columnTrack">
              <div
                :class="[
                  $style.columnFill,
                  isVerticalAccent(item)
                    ? $style.fillAccent
                    : props.fillVariant === 'accent'
                      ? $style.fillAccent
                      : $style.fillDark,
                ]"
                :style="{ height: barPercent(resolveVerticalValue(item), resolveVerticalMax(item)) }"
              />
            </div>
          </div>
          <span :class="$style.columnLabel">{{ item.label }}</span>
        </button>

        <div v-else :class="$style.columnStatic">
          <span :class="$style.columnValue">{{ resolveVerticalValue(item) }}</span>
          <div :class="$style.columnTrackWrap">
            <div :class="$style.columnTrack">
              <div
                :class="[
                  $style.columnFill,
                  isVerticalAccent(item)
                    ? $style.fillAccent
                    : props.fillVariant === 'accent'
                      ? $style.fillAccent
                      : $style.fillDark,
                ]"
                :style="{ height: barPercent(resolveVerticalValue(item), resolveVerticalMax(item)) }"
              />
            </div>
          </div>
          <span :class="$style.columnLabel">{{ item.label }}</span>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <Transition
        :enter-from-class="$style.enterFrom"
        :enter-active-class="$style.enterActive"
        :leave-active-class="$style.leaveActive"
        :leave-to-class="$style.leaveTo"
      >
        <div
          v-if="openIndex != null && !props.loading"
          ref="popoverRef"
          :class="$style.popover"
          :style="floatingStyles"
        >
          <div :class="$style.inputRow">
            <input
              ref="inputRef"
              v-model.number="editValue"
              type="number"
              :class="$style.input"
              @keydown="onInputKeydown"
            />
            <UiButtonIcon variant="tertiary" size="s" @click="decrement">
              <IconMinus />
            </UiButtonIcon>
            <UiButtonIcon variant="tertiary" size="s" @click="increment">
              <IconPlus />
            </UiButtonIcon>
          </div>
          <div :class="$style.actionRow">
            <UiButtonIcon variant="secondary" @click="closeEdit">
              <IconClose />
            </UiButtonIcon>
            <div :class="$style.applySlot">
              <UiButton variant="secondary" block @click="applyEdit">
                {{ props.applyLabel }}
              </UiButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.rootVertical {
  min-height: 0;
}

.row,
.rowStatic {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 4px;
  width: 100%;
  box-sizing: border-box;
}

.row {
  @include shared.reset-button;
  padding: 4px 8px 4px 4px;
  overflow: visible;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &:hover,
  &.rowActive {
    background: var(--ui2-shade-black-4);
  }
}

.label {
  width: 16px;
  flex: none;
  @include typography.style('data-xs-mono');
  color: var(--ui2-content-secondary, #655e60);
  text-align: right;
  white-space: nowrap;
}

.barArea {
  flex: 1;
  min-width: 0;
}

.track {
  position: relative;
  height: 4px;
  width: 100%;
  background: var(--ui2-shade-black-16);
  border-radius: 0 4px 4px 0;
  overflow: hidden;
}

.loadingBar {
  width: 100%;
  height: 8px;
  border-radius: 999px;
}

.fillDark {
  height: 100%;
  background: var(--ui2-shade-black-24);
  border-radius: 100px;
  transition: width 0.3s ease;
  flex: none;
}

.fillShade {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--ui2-shade-black-24);
  border-radius: 100px;
  transition: width 0.3s ease;
}

.fillAccent {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  height: 100%;
  background: var(--ui2-primitive-pine-forest-500);
  border-radius: 100px;
  transition: width 0.3s ease;
}

.stackedBarArea {
  flex: 1;
  min-width: 0;
  position: relative;
  padding: 3px 0;
}

.stackedRow {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 4px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.1s ease;

  &:hover {
    background: var(--ui2-shade-black-4);
  }
}

.stackedLegend {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 4px;
}

.stackedLegendItem {
  display: flex;
  align-items: center;
  gap: 4px;
  @include typography.style('data-xs-regular');
  color: var(--ui2-content-secondary, #655e60);
}

.stackedLegendSwatch1 {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--ui2-primitive-pine-forest-700);
  flex: none;
}

.stackedLegendSwatch2 {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--ui2-primitive-pine-forest-500);
  flex: none;
}

.stackedLegendTarget {
  width: 2px;
  height: 10px;
  border-radius: 1px;
  background: var(--ui2-content-primary, #010000);
  flex: none;
}

.stackedTooltip {
  position: absolute;
  top: 100%;
  left: 20px;
  z-index: 10;
  margin-top: 4px;
  padding: 8px 12px;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: nowrap;
  pointer-events: none;
}

.stackedTooltipRow {
  display: flex;
  align-items: center;
  gap: 6px;
  @include typography.style('data-xs-regular');
  color: var(--ui2-content-secondary, #655e60);
}

.stackedTooltipVal {
  @include typography.style('data-xs-semibold');
  color: var(--ui2-content-primary, #010000);
  margin-left: auto;
  padding-left: 12px;
}

.stackedValue {
  display: flex;
  align-items: baseline;
  flex: none;
  white-space: nowrap;
}

.stackedTrack {
  position: relative;
  height: 4px;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));
}

/* Stacked mode fills */

.fillStacked1 {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--ui2-primitive-pine-forest-700);
  border-radius: 4px;
  transition: width 0.3s ease;
  z-index: 2;
}

.fillStacked2 {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--ui2-primitive-pine-forest-500);
  border-radius: 4px;
  transition: width 0.3s ease;
  z-index: 1;
}

.targetMark {
  position: absolute;
  top: 50%;
  width: 2px;
  height: 12px;
  background: var(--ui2-content-primary, #010000);
  border-radius: 1px;
  transform: translate(-1px, -50%);
  z-index: 3;
}

.value {
  flex: none;
  @include typography.style('data-xs-medium');
  color: var(--ui2-content-primary, #010000);
  text-align: right;
  white-space: nowrap;
}

.loadingValue {
  width: 34px;
  height: 16px;
  border-radius: 999px;
  flex: none;
}

.max {
  flex: none;
  @include typography.style('data-xs-medium');
  color: var(--ui2-content-secondary, #655e60);
  text-align: right;
  white-space: nowrap;
}

.diff {
  flex: none;
  @include typography.style('data-xs-medium');
  text-align: right;
  white-space: nowrap;
}

.diffPositive {
  color: var(--ui2-data-pine-forest, #226b6c);
}

.diffNeutral {
  color: var(--ui2-content-secondary, #655e60);
}

.columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(24px, 1fr));
  gap: 8px;
  align-items: end;
  width: 100%;
}

.column,
.columnStatic {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.column {
  @include shared.reset-button;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &:hover,
  &.rowActive {
    background: var(--ui2-shade-black-4);
  }
}

.columnTrackWrap {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: 92px;
}

.columnTrack {
  display: flex;
  align-items: flex-end;
  width: 18px;
  height: 100%;
  background: var(--ui2-shade-black-16);
  border-radius: 4px 4px 0 0;
  overflow: hidden;
}

.columnFill {
  width: 100%;
  transition: height 0.3s ease;
  border-radius: 4px 4px 0 0;
}

.columnValue {
  @include typography.style('data-xs-medium');
  color: var(--ui2-content-primary, #010000);
  white-space: nowrap;
}

.columnLabel {
  @include typography.style('data-xs-mono');
  color: var(--ui2-content-secondary, #655e60);
  text-align: center;
  white-space: nowrap;
}

.loadingColumnValue {
  width: 28px;
  height: 16px;
  border-radius: 999px;
}

.loadingColumnBar {
  width: 18px;
  height: 100%;
  border-radius: 4px 4px 0 0;
}

.popover {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  width: 139px;
  box-sizing: border-box;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.04);
  z-index: 1000;
}

.enterActive,
.leaveActive {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.enterFrom,
.leaveTo {
  opacity: 0;
  transform: scale(0.95);
}

.inputRow {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 6px 6px 6px 8px;
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: var(--ui2-state-focus, #3355ff);
  }
}

.input {
  flex: 1;
  min-width: 48px;
  border: none;
  outline: none;
  background: none;
  @include typography.style('data-s-mono');
  color: var(--ui2-content-primary, #010000);
  padding: 0;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.actionRow {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.applySlot {
  flex: 1;
  min-width: 0;
}
</style>
